import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { voiceActorService } from '../../../services/voiceActorService';
import { useAuth } from '../../../contexts/AuthContext';
import UserAvatar from '../../../components/UserAvatar';

const TrendingPerformerCard = ({ performer, onClick, isAuthenticated, showRisingBadge }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(performer?.followerCount || 0);
  const [isFollowLoading, setIsFollowLoading] = useState(false);

  useEffect(() => {
    checkFollowStatus();
  }, [performer?.id, user?.id]);

  const checkFollowStatus = async () => {
    if (!user?.id || !performer?.id) return;
    
    try {
      const following = await voiceActorService?.isFollowing(performer?.id);
      setIsFollowing(following);
    } catch (err) {
      console.error('Error checking follow status:', err);
    }
  };

  const handleFollowClick = async (e) => {
    e?.stopPropagation();

    if (!isAuthenticated) {
      navigate('/registration');
      return;
    }

    try {
      setIsFollowLoading(true);
      
      if (isFollowing) {
        await voiceActorService?.unfollowVoiceActor(performer?.id);
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
      } else {
        await voiceActorService?.followVoiceActor(performer?.id);
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setIsFollowLoading(false);
    }
  };

  const getTrendIcon = () => {
    if (performer?.growthPercentage > 20) return 'TrendingUp';
    if (performer?.growthPercentage > 10) return 'ArrowUp';
    return 'Minus';
  };

  const getTrendColor = () => {
    if (performer?.growthPercentage > 20) return 'text-green-500';
    if (performer?.growthPercentage > 10) return 'text-blue-500';
    return 'text-gray-500';
  };

  return (
    <div
      onClick={() => onClick?.(performer?.id)}
      className="modern-card group hover:scale-[1.02] transition-all duration-300 cursor-pointer relative overflow-hidden"
    >
      {/* Rising Badge */}
      {showRisingBadge && performer?.isRising && (
        <div className="absolute top-3 right-3 z-10">
          <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
            <Icon name="Flame" size={12} />
            <span>Rising</span>
          </div>
        </div>
      )}

      {/* Performer Info */}
      <div className="flex items-start gap-4 mb-4">
        {performer?.headshotUrl ? (
          <img
            src={performer?.headshotUrl}
            alt={performer?.fullName || performer?.stageName}
            className="w-16 h-16 rounded-full object-cover border-2 border-purple-500/30 group-hover:border-purple-500 transition-colors"
          />
        ) : (
          <UserAvatar
            userId={performer?.id}
            name={performer?.fullName || performer?.stageName}
            size="w-16 h-16"
            textSize="text-xl"
            className="border-2 border-purple-500/30 group-hover:border-purple-500 transition-colors"
          />
        )}

        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-card-foreground mb-1 truncate group-hover:text-purple-400 transition-colors">
            {performer?.fullName || performer?.stageName}
          </h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Icon name="Users" size={14} />
            <span>{followerCount} followers</span>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center p-2 bg-background/50 rounded-lg">
          <div className={`flex items-center justify-center gap-1 mb-1 ${getTrendColor()}`}>
            <Icon name={getTrendIcon()} size={16} />
            <span className="text-sm font-bold">+{performer?.growthPercentage}%</span>
          </div>
          <p className="text-xs text-muted-foreground">Growth</p>
        </div>

        <div className="text-center p-2 bg-background/50 rounded-lg">
          <div className="flex items-center justify-center gap-1 mb-1 text-blue-500">
            <Icon name="Zap" size={16} />
            <span className="text-sm font-bold">{performer?.engagementVelocity}</span>
          </div>
          <p className="text-xs text-muted-foreground">Engagement</p>
        </div>

        <div className="text-center p-2 bg-background/50 rounded-lg">
          <div className="flex items-center justify-center gap-1 mb-1 text-purple-500">
            <Icon name="Star" size={16} />
            <span className="text-sm font-bold">{performer?.qualityScore}</span>
          </div>
          <p className="text-xs text-muted-foreground">Quality</p>
        </div>
      </div>

      {/* Specializations */}
      {performer?.specializations?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {performer?.specializations?.slice(0, 3)?.map((spec, index) => (
            <span
              key={index}
              className="text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full"
            >
              {spec}
            </span>
          ))}
        </div>
      )}

      {/* Follow Button */}
      <button
        onClick={handleFollowClick}
        disabled={isFollowLoading}
        className={`w-full py-2 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
          isFollowing
            ? 'bg-background border border-border text-card-foreground hover:bg-red-500/20 hover:border-red-500 hover:text-red-400'
            : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isFollowLoading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
        ) : (
          <>
            <Icon name={isFollowing ? 'UserMinus' : 'UserPlus'} size={16} />
            <span>{isFollowing ? 'Following' : 'Follow'}</span>
          </>
        )}
      </button>
    </div>
  );
};

export default TrendingPerformerCard;